export interface AuthoringVersionMarker {
  schemaVersion: 1;
  buildId: string;
}

export type CurrentAuthoringBuildResult =
  | { status: 'current'; buildId: string }
  | { status: 'stale'; loadedBuildId: string; deployedBuildId: string }
  | { status: 'unavailable'; reason: 'network' | 'http' | 'invalid-marker' };

interface CheckCurrentAuthoringBuildOptions {
  loadedBuildId?: string;
  freshnessCheckEnabled?: boolean;
  markerUrl?: string;
  fetcher?: typeof fetch;
}

function isVersionMarker(value: unknown): value is AuthoringVersionMarker {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return candidate.schemaVersion === 1
    && typeof candidate.buildId === 'string'
    && candidate.buildId.trim().length > 0;
}

function defaultMarkerUrl(): string {
  const baseUrl = new URL(import.meta.env.BASE_URL, window.location.origin);
  return new URL('version.json', baseUrl).toString();
}

/** Reads the deployed marker without trusting browser cache state. */
export async function checkCurrentAuthoringBuild(
  options: CheckCurrentAuthoringBuildOptions = {},
): Promise<CurrentAuthoringBuildResult> {
  const loadedBuildId = options.loadedBuildId ?? __AUTHORING_BUILD_ID__;
  const freshnessCheckEnabled = options.freshnessCheckEnabled
    ?? __AUTHORING_BUILD_CHECK_ENABLED__;
  if (!freshnessCheckEnabled) {
    return { status: 'current', buildId: loadedBuildId };
  }

  let response: Response;
  try {
    response = await (options.fetcher ?? fetch)(options.markerUrl ?? defaultMarkerUrl(), {
      cache: 'no-store',
      credentials: 'same-origin',
    });
  } catch {
    return { status: 'unavailable', reason: 'network' };
  }

  if (!response.ok) {
    return { status: 'unavailable', reason: 'http' };
  }

  let marker: unknown;
  try {
    marker = await response.json();
  } catch {
    return { status: 'unavailable', reason: 'invalid-marker' };
  }
  if (!isVersionMarker(marker)) {
    return { status: 'unavailable', reason: 'invalid-marker' };
  }

  if (marker.buildId === loadedBuildId) {
    return { status: 'current', buildId: loadedBuildId };
  }
  return {
    status: 'stale',
    loadedBuildId,
    deployedBuildId: marker.buildId,
  };
}
